import React, { useEffect, useState } from 'react';
import styles from './Styles/Photos.module.css';
import { getAllPhotos, getPhotosByUser, getPhotosByEvent, removePhoto, runPhotoMatch } from '../../Services/arndAPI';
import PhotoMatchesModal from '../../Components/PhotoMatchesModal/PhotoMatchesModal';
import Loader from '../../Components/Loader/Loader';

const S3_BASE = 'https://arnd-development.s3.us-west-1.amazonaws.com/';

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [filter, setFilter] = useState({ userId: '', eventId: '' });
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [modalAttendees, setModalAttendees] = useState([]);

  const handleSingleMatch = async (photo) => {
    setStatus('Running match...');
    try {
      await runPhotoMatch({ eventId: photo.event?.id, photoId: photo.id });
      setStatus('Match completed!');
      fetchPhotos();
    } catch (err) {
      setStatus(err.message || 'Error running match.');
    }
  };

  const fetchPhotos = async () => {
    setLoading(true);
    setStatus('');
    try {
      let data;
      if (filter.userId) {
        data = await getPhotosByUser(filter.userId);
      } else if (filter.eventId) {
        data = await getPhotosByEvent(filter.eventId);
      } else {
        data = await getAllPhotos();
      }
      setPhotos(data);
    } catch {
      setStatus('Error loading photos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if a filter is applied
    if (filter.userId || filter.eventId) {
      fetchPhotos();
    } else {
      setPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleRemove = async (photoId) => {
    if (!window.confirm('Are you sure you want to remove this photo?')) return;
    setStatus('Removing photo...');
    try {
      await removePhoto(photoId);
      setStatus('Photo removed.');
      fetchPhotos();
    } catch {
      setStatus('Error removing photo.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleMatchAll = async () => {
    if (!filter.eventId) return;
    setStatus('Matching all photos...');
    try {
      await runPhotoMatch({ eventId: filter.eventId });
      setStatus('All unmatched photos matched!');
      fetchPhotos();
    } catch (err) {
      setStatus(err.message || 'Error running match for all.');
    }
  };

  return (
    <div className={styles.container}>
      <h2>Photo Management</h2>
      <form className={styles.filters} onSubmit={e => { e.preventDefault(); fetchPhotos(); }}>
        <input
          type="text"
          name="userId"
          placeholder="Search by Photographer ID"
          value={filter.userId}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="eventId"
          placeholder="Search by Event ID"
          value={filter.eventId}
          onChange={handleFilterChange}
        />
        <button type="submit">Search</button>
      </form>
      {filter.eventId && !filter.userId && photos.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0 1.5rem 0' }}>
          <button
            className={styles.matchBtn}
            style={{ minWidth: 220, fontSize: '1.08rem', fontWeight: 600, boxShadow: '0 2px 8px rgba(16,185,129,0.10)' }}
            onClick={handleMatchAll}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
              Match All Unmatched Photos
            </span>
          </button>
        </div>
      )}
      {loading ? <Loader /> : (
        (filter.userId || filter.eventId) ? (
          <div className={styles.cardGrid}>
            {photos.length === 0 ? <div className={styles.emptyPhotos}><svg width="48" height="48" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ marginBottom: '1rem' }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="2.5" /><path d="M21 15l-5-5L5 21" /></svg><div style={{ fontSize: '1.2rem', color: '#374151', fontWeight: 500 }}>No photos found</div></div> : (
              <table className={styles.photoTable}>
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Event</th>
                    <th>Created</th>
                    <th>ID</th>
                    <th>Matches</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {photos.map((photo, idx) => (
                    <tr key={photo.id + '-' + idx}>
                      <td>
                        <img
                          src={S3_BASE + photo.mediumUrl}
                          alt={photo.event?.name || 'Event'}
                          style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', background: '#f3f4f6', border: '1px solid #e5e7eb' }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{photo.event?.name || '-'}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>{photo.event?.id || '-'}</div>
                      </td>
                      <td>{new Date(photo.createdAt).toLocaleString()}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.97rem' }}>{photo.id}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.97rem' }}>
                        {photo.hasMatch && photo.matchedAttendees && photo.matchedAttendees.length > 0 ? (
                          <span
                            style={{ color: '#0d58ecff', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                              setModalAttendees(photo.matchedAttendees);
                              setShowMatchesModal(true);
                            }}
                          >
                            Attendees
                          </span>
                        ) : (
                          'None'
                        )}
                      </td>
                      <td style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className={styles.matchBtn} onClick={() => handleSingleMatch(photo)}>
                          Run Match
                        </button>
                        <button className={styles.removeBtn} onClick={() => handleRemove(photo.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className={styles.emptyPhotos}>
            <svg width="48" height="48" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ marginBottom: '1rem' }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="2.5" /><path d="M21 15l-5-5L5 21" /></svg>
            <div style={{ fontSize: '1.2rem', color: '#374151', fontWeight: 500 }}>Search by event or photographer to view photos</div>
            <div style={{ color: '#6b7280', fontSize: '1rem', marginTop: '0.5rem' }}>Please enter a Photographer ID or Event ID above.</div>
          </div>
        )
      )}
      {status && <div className={styles.status}>{status}</div>}
      {showMatchesModal && (
        <PhotoMatchesModal
          attendees={modalAttendees}
          onClose={() => setShowMatchesModal(false)}
        />
      )}
    </div>
  );
}
